import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import logger from "../utils/logger";
import { Users, AlertCircle, FileSearch, TrendingUp, Shield, Activity, Key, Search, RefreshCw, Plus, UserPlus, FilePlus, FolderPlus, ExternalLink } from "lucide-react";
import { useCountUp } from "../components/dashboard/useCountUp";
import { SparklineChart } from "../components/dashboard/SparklineChart";
import { RiskDonut } from "../components/dashboard/RiskDonut";
import { ActivityFeed } from "../components/dashboard/ActivityFeed";
import { IndiaHeatMap } from "../components/dashboard/IndiaHeatMap";
import { IntegrationCards } from "../components/dashboard/IntegrationCards";
import { SLAComplianceWidget } from "../components/dashboard/SLAComplianceWidget";
import { RegulatoryCalendar } from "../components/dashboard/RegulatoryCalendar";
import { AutoCasesTodayWidget } from "../components/dashboard/AutoCasesTodayWidget";

const API = `${window.location.origin}/api`;

function StatCard({ title, value, icon: Icon, color, trendData, trendKey, testId, to }) {
  const animatedValue = useCountUp(value);
  const navigate = useNavigate();
  const handleClick = to ? () => navigate(to) : undefined;
  return (
    <div
      className="card-aml"
      data-testid={testId}
      onClick={handleClick}
      style={{
        position: "relative", overflow: "hidden",
        cursor: to ? "pointer" : "default",
        transition: "border-color 0.2s ease, transform 0.2s ease",
      }}
      onMouseEnter={(e) => { if (to) { e.currentTarget.style.borderColor = color; e.currentTarget.style.transform = "translateY(-1px)"; } }}
      onMouseLeave={(e) => { if (to) { e.currentTarget.style.borderColor = ""; e.currentTarget.style.transform = ""; } }}
    >
      <div className="flex items-center justify-between mb-3">
        <div style={{
          width: "40px", height: "40px", borderRadius: "10px",
          background: `${color}15`, display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <Icon style={{ color, width: "20px", height: "20px" }} />
        </div>
        {to && (
          <ExternalLink style={{ width: 13, height: 13, color: "#475569", opacity: 0.7 }} />
        )}
      </div>
      <div style={{ fontSize: "10px", fontWeight: "600", textTransform: "uppercase", letterSpacing: "1.5px", color: "#475569", marginBottom: "2px" }}>
        {title}
      </div>
      <div className="flex items-end justify-between">
        <div style={{ fontSize: "28px", fontWeight: "700", color: "#f1f5f9", lineHeight: 1 }}>
          {animatedValue}
        </div>
        {trendData && trendData.length > 0 && (
          <div style={{ width: "80px", height: "32px", opacity: 0.8 }} title="7-day trend (audit logs)">
            <SparklineChart data={trendData} color={color} dataKey={trendKey} />
          </div>
        )}
      </div>
    </div>
  );
}

function QuickActionsDropdown() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const actions = [
    { label: "Add Customer", icon: UserPlus, to: "/customers?new=1", testId: "quick-action-add-customer" },
    { label: "New Screening", icon: FilePlus, to: "/screening", testId: "quick-action-new-screening" },
    { label: "New Case", icon: FolderPlus, to: "/cases?new=1", testId: "quick-action-new-case" },
    { label: "New Onboarding Link", icon: Plus, to: "/onboarding-tokens?new=1", testId: "quick-action-new-onboarding" },
  ];

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen(v => !v)}
        data-testid="quick-actions-btn"
        style={{
          background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
          border: "none", borderRadius: 8, padding: "7px 14px",
          color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer",
          display: "flex", alignItems: "center", gap: 6,
        }}
      >
        <Plus style={{ width: 14, height: 14 }} />
        Quick Actions
      </button>
      {open && (
        <div data-testid="quick-actions-menu" style={{
          position: "absolute", top: "calc(100% + 6px)", right: 0, minWidth: 220,
          background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10,
          boxShadow: "0 10px 40px rgba(0,0,0,0.6)", padding: 6, zIndex: 50,
        }}>
          {actions.map(a => {
            const Icon = a.icon;
            return (
              <button
                key={a.label}
                data-testid={a.testId}
                onClick={() => { setOpen(false); navigate(a.to); }}
                style={{
                  width: "100%", display: "flex", alignItems: "center", gap: 10,
                  padding: "10px 12px", background: "transparent", border: "none",
                  borderRadius: 6, cursor: "pointer", color: "#cbd5e1", fontSize: 13,
                  textAlign: "left", transition: "background 0.15s ease",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#1e2530"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              >
                <Icon style={{ width: 15, height: 15, color: "#2563eb" }} />
                <span>{a.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [trends, setTrends] = useState([]);
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [screenQuery, setScreenQuery] = useState("");

  // API, axios, logger are module-level constants — [] is intentional (mount-only fetch)
  const fetchAll = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const [statsRes, trendsRes, feedRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { withCredentials: true }),
        axios.get(`${API}/dashboard/trends`, { withCredentials: true }),
        axios.get(`${API}/dashboard/activity-feed`, { withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setTrends(trendsRes.data.trends || []);
      setFeed(feedRes.data.feed || []);
      setLastUpdated(new Date());
    } catch (error) {
      logger.error("Failed to fetch dashboard data:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleQuickScreen = () => {
    if (screenQuery.trim()) {
      navigate(`/screening?q=${encodeURIComponent(screenQuery.trim())}`);
    }
  };

  function formatTimeAgo(date) {
    const secs = Math.floor((Date.now() - date.getTime()) / 1000);
    if (secs < 10) return "just now";
    if (secs < 60) return `${secs}s ago`;
    if (secs < 3600) return `${Math.floor(secs / 60)} min ago`;
    return `${Math.floor(secs / 3600)}h ago`;
  }

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div style={{ color: "#94a3b8" }}>Loading...</div></div>;
  }

  const scr = stats?.screening_stats || {};
  const kyc = stats?.kyc_stats || {};
  const cdd = stats?.cdd_breakdown || {};
  const api = stats?.api_usage || {};
  const integrations = stats?.integrations || {};

  return (
    <div data-testid="dashboard-page">
      {/* Quick Screen Bar */}
      <div data-testid="quick-screen-bar" style={{
        background: "#0d1117", border: "1px solid #1e2530", borderRadius: "12px",
        padding: "6px 6px 6px 20px", marginBottom: "24px",
        display: "flex", alignItems: "center",
      }}>
        <Search style={{ width: "18px", height: "18px", color: "#475569", flexShrink: 0 }} />
        <input
          value={screenQuery}
          onChange={(e) => setScreenQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleQuickScreen()}
          placeholder="Enter name or ID to screen instantly..."
          data-testid="quick-screen-input"
          style={{
            flex: 1, background: "transparent", border: "none", outline: "none",
            padding: "10px 14px", color: "#f1f5f9", fontSize: "14px",
          }}
        />
        <button
          onClick={handleQuickScreen}
          data-testid="quick-screen-btn"
          style={{
            background: "linear-gradient(135deg, #2563eb, #1d4ed8)", border: "none",
            borderRadius: "8px", padding: "10px 24px", color: "#fff",
            fontSize: "13px", fontWeight: "600", cursor: "pointer",
            display: "flex", alignItems: "center", gap: "6px",
            transition: "opacity 0.15s",
          }}
        >
          <Search style={{ width: "14px", height: "14px" }} />
          Screen
        </button>
      </div>

      {/* Header with Last Updated */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 style={{ fontSize: "26px", fontWeight: "700", letterSpacing: "-0.5px", color: "#f1f5f9", marginBottom: "4px" }} data-testid="dashboard-title">
            Compliance Dashboard
          </h1>
          <p style={{ color: "#94a3b8", fontSize: "14px" }}>Real-time overview of your AML/KYC operations</p>
        </div>
        <div className="flex items-center gap-2" data-testid="dashboard-header-actions">
          {lastUpdated && (
            <span style={{ fontSize: "11px", color: "#475569" }}>
              Last updated: {formatTimeAgo(lastUpdated)}
            </span>
          )}
          <button
            onClick={() => fetchAll(true)}
            disabled={refreshing}
            data-testid="refresh-dashboard-btn"
            style={{
              background: "#0d1117", border: "1px solid #1e2530", borderRadius: "8px",
              padding: "6px 10px", cursor: "pointer", color: "#94a3b8",
              display: "flex", alignItems: "center", gap: "4px", fontSize: "11px",
              transition: "all 0.15s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#2563eb"; e.currentTarget.style.color = "#2563eb"; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#1e2530"; e.currentTarget.style.color = "#94a3b8"; }}
          >
            <RefreshCw style={{ width: "13px", height: "13px", animation: refreshing ? "spin 1s linear infinite" : "none" }} />
            Refresh
          </button>
          <QuickActionsDropdown />
        </div>
      </div>

      {/* Stats Row with Sparklines */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
        <StatCard title="Total Customers" value={stats?.total_customers || 0} icon={Users} color="#2563eb" trendData={trends} trendKey="customers" testId="stat-card-total-customers" to="/customers" />
        <StatCard title="Pending Reviews" value={stats?.pending_reviews || 0} icon={FileSearch} color="#f59e0b" trendData={trends} trendKey="screenings" testId="stat-card-pending-reviews" to="/customers?filter=pending" />
        <StatCard title="High Risk" value={stats?.high_risk_customers || 0} icon={AlertCircle} color="#ef4444" trendData={trends} trendKey="risk" testId="stat-card-high-risk" to="/customers?filter=high" />
        <StatCard title="Open Cases" value={stats?.open_cases || 0} icon={TrendingUp} color="#10b981" trendData={trends} trendKey="cases" testId="stat-card-open-cases" to="/cases?status=open" />
      </div>

      <AutoCasesTodayWidget />

      {/* Row 2: Donut + Screening + KYC */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
        <div className="card-aml" data-testid="risk-distribution">
          <h2 style={{ fontSize: "13px", fontWeight: "700", color: "#f1f5f9", marginBottom: "12px" }}>Risk Distribution</h2>
          <RiskDonut data={stats?.risk_distribution || {}} />
        </div>

        <div className="card-aml" data-testid="screening-stats">
          <div className="flex items-center justify-between mb-4">
            <h2 style={{ fontSize: "13px", fontWeight: "700", color: "#f1f5f9", margin: 0 }}>Screening Alerts</h2>
            {stats?.watchlist_alerts?.last_24h > 0 && (
              <span style={{ background: "#dc262622", color: "#dc2626", border: "1px solid #dc262655", borderRadius: 4, padding: "2px 8px", fontSize: 10, fontWeight: 700 }} data-testid="watchlist-24h-badge">
                +{stats.watchlist_alerts.last_24h} in 24h
              </span>
            )}
          </div>
          <div className="space-y-4">
            {[
              { label: "Watchlist Matches", value: stats?.watchlist_alerts?.open || 0, icon: AlertCircle, color: "#dc2626", to: "/watchlist", testId: "stat-watchlist-matches" },
              { label: "Sanctions Matches", value: scr.sanctions_matches || 0, icon: AlertCircle, color: "#ef4444", to: "/customers?filter=sanctions", testId: "stat-sanctions-matches" },
              { label: "PEP Matches", value: scr.pep_matches || 0, icon: Shield, color: "#f59e0b", to: "/customers?filter=pep", testId: "stat-pep-matches" },
              { label: "Adverse Media", value: scr.adverse_media_hits || 0, icon: FileSearch, color: "#8b5cf6", to: "/adverse-media", testId: "stat-adverse-media" },
            ].map((s) => {
              const SIcon = s.icon;
              const inner = (
                <div key={s.label} className="flex items-center justify-between" data-testid={s.testId}>
                  <div className="flex items-center gap-3">
                    <div style={{ width: "36px", height: "36px", borderRadius: "8px", background: `${s.color}18`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <SIcon style={{ width: 16, height: 16, color: s.color }} />
                    </div>
                    <span style={{ fontSize: "13px", color: "#94a3b8" }}>{s.label}</span>
                  </div>
                  <span style={{ fontSize: "20px", fontWeight: 700, color: s.value > 0 ? s.color : "#475569" }}>{s.value}</span>
                </div>
              );
              return s.to ? (
                <Link key={s.label} to={s.to} style={{ textDecoration: "none", cursor: "pointer", display: "block" }}>{inner}</Link>
              ) : inner;
            })}
          </div>
        </div>

        <div className="card-aml" data-testid="kyc-cdd-stats">
          <h2 style={{ fontSize: "13px", fontWeight: "700", color: "#f1f5f9", marginBottom: "16px" }}>KYC & Due Diligence</h2>
          <div className="space-y-3 mb-4">
            {[
              { label: "Verifications Run", value: kyc.total || 0, color: "#2563eb" },
              { label: "Verified", value: kyc.verified || 0, color: "#10b981" },
              { label: "Failed", value: kyc.failed || 0, color: "#ef4444" },
            ].map(k => (
              <div key={k.label} className="flex items-center justify-between">
                <span style={{ fontSize: "12px", color: "#94a3b8" }}>{k.label}</span>
                <span style={{ fontSize: "16px", fontWeight: 700, color: k.color }}>{k.value}</span>
              </div>
            ))}
          </div>
          <div style={{ borderTop: "1px solid #1e2530", paddingTop: "12px" }}>
            <div style={{ fontSize: "11px", color: "#475569", marginBottom: "8px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "1px" }}>CDD Tiers</div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { label: "SDD", val: cdd.sdd || 0, color: "#10b981" },
                { label: "Standard", val: cdd.standard_cdd || 0, color: "#2563eb" },
                { label: "EDD", val: cdd.edd || 0, color: "#ef4444" },
              ].map(t => (
                <div key={t.label} style={{ textAlign: "center", padding: "8px", background: "#080c12", borderRadius: "6px" }}>
                  <div style={{ fontSize: "18px", fontWeight: 700, color: t.color }}>{t.val}</div>
                  <div style={{ fontSize: "10px", color: "#475569" }}>{t.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Row 3: Extended Stats + Regulatory Calendar */}
      <div className="mb-6">
        <RegulatoryCalendar />
      </div>

      {/* Row 4: SLA Compliance Monitor */}
      <div className="card-aml mb-6" data-testid="sla-compliance-section">
        <SLAComplianceWidget />
      </div>

      {/* Row 4: Heat Map + Activity Feed */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
        <div className="card-aml" data-testid="screening-heatmap">
          <h2 style={{ fontSize: "13px", fontWeight: "700", color: "#f1f5f9", marginBottom: "12px" }}>Screening Concentration</h2>
          <IndiaHeatMap />
        </div>

        <div className="card-aml" data-testid="activity-feed" style={{ maxHeight: "360px", display: "flex", flexDirection: "column" }}>
          <h2 style={{ fontSize: "13px", fontWeight: "700", color: "#f1f5f9", marginBottom: "12px" }}>Live Activity Feed</h2>
          <div style={{ flex: 1, overflowY: "auto" }}>
            <ActivityFeed feed={feed} />
          </div>
        </div>
      </div>

      {/* Row 5: Integrations + API Usage */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="card-aml" data-testid="integration-status">
          <h2 style={{ fontSize: "13px", fontWeight: "700", color: "#f1f5f9", marginBottom: "16px" }}>Integration Status</h2>
          <IntegrationCards integrations={integrations} />
        </div>

        {(api.total_api_calls !== undefined || api.recent_calls) && (
          <div className="card-aml" data-testid="api-usage-stats">
            <div className="flex items-center justify-between mb-3">
              <h2 style={{ fontSize: "13px", fontWeight: "700", color: "#f1f5f9" }}>API Usage</h2>
              <Link to="/developers" data-testid="api-usage-view-all" style={{
                fontSize: 10, color: "#2563eb", textDecoration: "none", fontWeight: 600,
              }}>View All →</Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-4">
              {[
                { label: "Total Calls", value: api.total_api_calls || 0, icon: Activity, color: "#2563eb" },
                { label: "24h Calls", value: api.calls_24h || 0, icon: TrendingUp, color: "#10b981" },
                { label: "Avg Latency", value: api.avg_response_ms ? `${api.avg_response_ms}ms` : "—", icon: Activity, color: "#FFD700" },
                { label: "Success Rate", value: api.success_rate !== undefined ? `${api.success_rate}%` : "—", icon: Shield, color: "#10b981" },
              ].map(s => {
                const SIcon = s.icon;
                return (
                  <div key={s.label} data-testid={`api-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`} style={{ padding: "12px", background: "#080c12", borderRadius: "8px" }}>
                    <SIcon style={{ width: 14, height: 14, color: s.color, marginBottom: 4 }} />
                    <div style={{ fontSize: "16px", fontWeight: 700, color: "#f1f5f9", lineHeight: 1 }}>{s.value}</div>
                    <div style={{ fontSize: "9px", color: "#475569", marginTop: 2, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>{s.label}</div>
                  </div>
                );
              })}
            </div>

            {/* Recent Calls Log */}
            {Array.isArray(api.recent_calls) && api.recent_calls.length > 0 && (
              <div data-testid="api-recent-calls">
                <div style={{ fontSize: "10px", fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: 8 }}>
                  Recent Calls
                </div>
                <div style={{ fontFamily: "ui-monospace, SFMono-Regular, monospace", fontSize: 11 }}>
                  {api.recent_calls.map((call) => {
                    const status = call.status_code ?? 200;
                    const ok = status >= 200 && status < 300;
                    const ts = call.timestamp ? new Date(call.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—";
                    return (
                      <div key={`${call.timestamp}-${call.endpoint}`} className="flex items-center justify-between" style={{
                        padding: "6px 10px", borderBottom: "1px solid #1e2530",
                      }}>
                        <div className="flex items-center gap-2" style={{ minWidth: 0, flex: 1 }}>
                          <span style={{
                            display: "inline-block", width: 8, height: 8, borderRadius: "50%",
                            background: ok ? "#10b981" : "#ef4444", flexShrink: 0,
                          }} />
                          <span style={{ color: "#94a3b8", whiteSpace: "nowrap", flexShrink: 0 }}>{ts}</span>
                          <span style={{ color: "#cbd5e1", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{call.endpoint}</span>
                        </div>
                        <div className="flex items-center gap-2" style={{ flexShrink: 0 }}>
                          <span style={{ color: ok ? "#10b981" : "#ef4444", fontWeight: 700 }}>{status}</span>
                          <span style={{ color: "#475569" }}>{call.response_time_ms ?? 0}ms</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
